import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { getApiErrorMessage } from '@/shared/lib';

/**
 * Shown when the convoy context call fails.
 *
 * When the backend says why — typically "This journey cannot be received since
 * it is not yet dispatched" for a convoy one of whose trucks never left — that
 * reason is shown as is, with the way out: the consignments of the trucks that
 * did leave are still receivable one by one from the dashboard rows. Without a
 * reason (network, 5xx) it falls back to the generic close-and-retry text.
 */
export function ReceiveConvoyContextError({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const fallback = t(
    'receive.confirm.error.contextFailed',
    'Failed to load the receipt details. Please close the dialog and try again.'
  );
  const message = getApiErrorMessage(error, fallback);

  return (
    <Alert variant="destructive">
      <AlertDescription className="flex flex-col gap-1">
        <span>{message}</span>
        {message !== fallback && (
          <span>
            {t(
              'receive.convoy.error.receiveIndividually',
              'The consignments of this convoy can still be received one by one from its rows on the dashboard.'
            )}
          </span>
        )}
      </AlertDescription>
    </Alert>
  );
}
